import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import {
  colors as DS,
  TechLabel,
  StatusBadge,
  PrimaryButton,
  BlueprintCard,
  SkeuInput,
} from "../design-system";
import { backendApi } from "./api/backendApi";

const ACCENT = DS.product.hrms;

// Renders the "set your password" screen reached from the approval email:
//   {FRONTEND_URL}/set-password?token=xxxx
// On success the account is activated + logged in, then we send the user into
// the HRMS app.
export default function SetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("This link is missing its token. Please use the link from your email.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      await backendApi.auth.setPassword({ token, password });
      setDone(true);
      setTimeout(() => navigate("/hrms"), 1200);
    } catch (err) {
      setError(err.message || "Could not set your password. The link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: DS.paper,
        color: DS.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      <BlueprintCard accent={ACCENT} className="w-full max-w-md" spatial>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 flex items-center justify-center" style={{ background: DS.forest }}>
            <ShieldCheck className="w-6 h-6" style={{ color: ACCENT }} />
          </div>
          <div>
            <span className="text-lg font-extrabold tracking-tight">WebIdeal</span>
            <TechLabel style={{ color: ACCENT }}>module · hrms</TechLabel>
          </div>
        </div>

        {done ? (
          <div className="py-6">
            <StatusBadge color={ACCENT}>account activated</StatusBadge>
            <h2 className="mt-3 text-2xl font-extrabold">You're all set</h2>
            <p className="mt-2 text-sm" style={{ color: DS.grid }}>
              Redirecting you to HRMS…
            </p>
          </div>
        ) : (
          <>
            <TechLabel style={{ color: `${DS.grid}99` }}>secure onboarding</TechLabel>
            <h2 className="mt-2 text-2xl font-extrabold">Set your password</h2>
            <p className="mt-1.5 text-sm" style={{ color: DS.grid }}>
              Choose a password to activate your account.
            </p>

            <form className="mt-6 space-y-4" onSubmit={submit}>
              <div>
                <TechLabel as="label" style={{ color: `${DS.grid}99` }} className="block mb-1.5">
                  New password
                </TechLabel>
                <SkeuInput
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <TechLabel as="label" style={{ color: `${DS.grid}99` }} className="block mb-1.5">
                  Confirm password
                </TechLabel>
                <SkeuInput
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                />
              </div>

              {error && (
                <p className="text-sm font-semibold" style={{ color: DS.coral }}>
                  {error}
                </p>
              )}

              <PrimaryButton type="submit" className="w-full" disabled={busy}>
                {busy ? "Activating…" : "Set password & continue"}
              </PrimaryButton>
            </form>
          </>
        )}
      </BlueprintCard>
    </div>
  );
}
